
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { triggerHaptic } from '../utils/haptics';

const BetSlipContext = createContext();

export const useBetSlip = () => {
  const context = useContext(BetSlipContext);
  if (!context) {
    throw new Error('useBetSlip must be used within a BetSlipProvider');
  }
  return context;
};

const generateVisitorId = () => {
  if (typeof window === 'undefined') return null;
  let visitorId = localStorage.getItem('piks_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('piks_visitor_id', visitorId);
  }
  return visitorId;
};

const generateSessionId = () => {
  if (typeof window === 'undefined') return null;
  let sessionId = sessionStorage.getItem('piks_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('piks_session_id', sessionId);
  }
  return sessionId;
};

export const BetSlipProvider = ({ children }) => {
  const { data: session } = useSession();
  const [betSlip, setBetSlipState] = useState([]);
  const [showBetSlip, setShowBetSlip] = useState(false);

  // Synchronously-updated mirror of the slip. Odds-button taps commit their
  // state update inside a React transition (so the huge board re-render never
  // blocks the tap's paint), which means two quick taps can both fire before
  // the first render commits — reading `betSlip` from the closure would lose
  // the first bet. All mutations read/write this ref first, then push to state.
  const betSlipRef = useRef([]);
  const commitSlip = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(betSlipRef.current) : next;
    betSlipRef.current = resolved;
    setBetSlipState(resolved);
    return resolved;
  }, []);
  // Drop-in replacement for the old setBetSlip so external consumers keep
  // working while staying in sync with the ref.
  const setBetSlip = commitSlip;

  const userId = session?.user?.id || null;

  const trackBetSlipEvent = useCallback(async (eventType, betData) => {
    if (typeof window === 'undefined') return;
    try {
      await fetch('/api/analytics/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          events: [{
            type: eventType,
            userId,
            visitorId: generateVisitorId(),
            sessionId: generateSessionId(),
            data: betData,
            pageUrl: window.location.pathname,
          }]
        }),
      });
    } catch (error) {
      console.error('Failed to track bet slip event:', error);
    }
  }, [userId]);

  // Load bet slip from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('betSlip');
    const savedShowState = localStorage.getItem('showBetSlip');
    if (saved) {
      try {
        commitSlip(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to parse saved bet slip:', error);
      }
    }
    if (savedShowState) {
      setShowBetSlip(JSON.parse(savedShowState));
    }
  }, []);

  // Save bet slip to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('betSlip', JSON.stringify(betSlip));
  }, [betSlip]);

  // Save show bet slip state to localStorage
  useEffect(() => {
    localStorage.setItem('showBetSlip', JSON.stringify(showBetSlip));
  }, [showBetSlip]);

  useEffect(() => {
    const handleOpenBetSlip = () => {
      setShowBetSlip(true);
    };

    window.addEventListener('openBetSlip', handleOpenBetSlip);
    return () => {
      window.removeEventListener('openBetSlip', handleOpenBetSlip);
    };
  }, []);

  const addToBetSlip = useCallback((game, betType, odds, selection) => {
    // Haptic FIRST and synchronously: iOS only fires the switch-haptic while
    // the page has transient user activation from the tap.
    triggerHaptic('tap');

    const betId = `${game.id}-${betType}-${selection}`;
    const currentSlip = betSlipRef.current;
    const existingBetIndex = currentSlip.findIndex(bet => bet.id === betId);
    const oddsValue = typeof odds === 'object' ? odds.odds || odds.value || 0 : odds;

    if (existingBetIndex >= 0) {
      const removedBet = currentSlip[existingBetIndex];
      commitSlip(currentSlip.filter(bet => bet.id !== betId));
      trackBetSlipEvent('bet_removed_toggle', {
        betId,
        matchup: removedBet.matchup,
        selection: removedBet.selection,
        odds: removedBet.odds,
        betType: removedBet.betType,
      });
    } else {
      const filteredSlip = currentSlip.filter(bet => {
        const isSameGameAndType = bet.gameId === game.id && bet.betType === betType;
        if (isSameGameAndType) {
          trackBetSlipEvent('bet_removed_conflict', {
            betId: bet.id,
            matchup: bet.matchup,
            selection: bet.selection,
            odds: bet.odds,
            betType: bet.betType,
            replacedBy: selection,
          });
        }
        return !isSameGameAndType;
      });
      
      const capturedIsLive = !!(game.isLive || game.status === 'IN_PROGRESS');
      const capturedAwayScore = game.awayScore ?? game.scores?.away?.total ?? 0;
      const capturedHomeScore = game.homeScore ?? game.scores?.home?.total ?? 0;
      
      const newBet = {
        id: betId,
        gameId: game.id,
        // Special single-title events (e.g. Home Run Derby) pass matchupName
        // directly; regular games compose the away @ home pair.
        matchup: game.matchupName || `${game.awayTeamFull || game.awayTeam} @ ${game.homeTeamFull || game.homeTeam}`,
        betType,
        selection,
        odds: oddsValue,
        stake: 0,
        isLive: capturedIsLive,
        awayTeam: game.awayTeam,
        homeTeam: game.homeTeam,
        awayTeamFull: game.awayTeamFull || game.awayTeam,
        homeTeamFull: game.homeTeamFull || game.homeTeam,
        awayScore: capturedAwayScore,
        homeScore: capturedHomeScore,
        gameTime: game.time || '',
        gameStart: game.startTime || null,
        sportName: game.sportName || '',
        sport: game.sport || game.sportKey || game.sport_key || ''
      };
      
      commitSlip([...filteredSlip, newBet]);
      trackBetSlipEvent('bet_added', {
        betId,
        matchup: newBet.matchup,
        selection,
        odds: oddsValue,
        betType,
        gameId: game.id,
        awayTeam: game.awayTeam,
        homeTeam: game.homeTeam,
      });
    }
  }, [commitSlip, trackBetSlipEvent]);

  const removeBet = (betId) => {
    triggerHaptic('tap');
    const currentSlip = betSlipRef.current;
    const removedBet = currentSlip.find(bet => bet.id === betId);
    commitSlip(currentSlip.filter(bet => bet.id !== betId));
    if (removedBet) {
      trackBetSlipEvent('bet_removed', {
        betId,
        matchup: removedBet.matchup,
        selection: removedBet.selection,
        odds: removedBet.odds,
        betType: removedBet.betType,
        stake: removedBet.stake,
      });
    }
  };

  const updateStake = (betId, stake) => {
    const currentSlip = betSlipRef.current;
    const bet = currentSlip.find(b => b.id === betId);
    const newStake = parseFloat(stake) || 0;
    commitSlip(currentSlip.map(b => 
      b.id === betId ? { ...b, stake: newStake } : b
    ));
    if (bet && newStake > 0) {
      trackBetSlipEvent('stake_updated', {
        betId,
        matchup: bet.matchup,
        selection: bet.selection,
        oldStake: bet.stake,
        newStake,
        odds: bet.odds,
      });
    }
  };

  const clearBetSlip = () => {
    commitSlip([]);
    setShowBetSlip(false);
  };

  const isBetInSlip = (game, betType, selection) => {
    const betId = `${game.id}-${betType}-${selection}`;
    return betSlip.some(bet => bet.id === betId);
  };

  return (
    <BetSlipContext.Provider value={{
      betSlip,
      setBetSlip,
      showBetSlip,
      setShowBetSlip,
      addToBetSlip,
      removeBet,
      updateStake,
      clearBetSlip,
      isBetInSlip
    }}>
      {children}
    </BetSlipContext.Provider>
  );
};
